import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { admAPI } from "../../lib/admApi";
import { toast } from "sonner";
import {
  School, User, Users, GraduationCap, Heart, FileCheck, ChevronRight, ChevronLeft,
  Save, Send, AlertTriangle, CheckCircle2,
} from "lucide-react";

/**
 * Public multi-step Application. Path:
 *   /education360/apply/:school
 * Creates a draft on load if no token in localStorage; then autosaves each step.
 */
const STEPS = [
  { id: "student", label: "Student", icon: User },
  { id: "family", label: "Family", icon: Users },
  { id: "history", label: "Academic", icon: GraduationCap },
  { id: "medical", label: "Medical & Special", icon: Heart },
  { id: "essay", label: "Essay", icon: FileCheck },
  { id: "docs", label: "Documents", icon: FileCheck },
  { id: "declare", label: "Declaration", icon: CheckCircle2 },
];

const YEAR_GROUPS = ["Year 7", "Year 8", "Year 9", "Year 10", "Year 11", "Year 12", "Year 13"];

function tokenKey(school) { return `edu360.applicant.${school}.token`; }

export default function PublicApply() {
  const { school } = useParams();
  const nav = useNavigate();
  const [applicant, setApplicant] = useState(null);
  const [step, setStep] = useState(0);
  const [tenant, setTenant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [missing, setMissing] = useState([]);
  const debouncer = useRef(null);

  useEffect(() => {
    admAPI.prospectus(school).then(({ data }) => setTenant(data.tenant)).catch(() => {});
    const stored = localStorage.getItem(tokenKey(school));
    if (stored) {
      admAPI.loadApplicantPortal(stored).then(({ data }) => {
        setApplicant(data.applicant);
        if (data.applicant.stage !== "application_draft") {
          // Already submitted — send to portal
          nav(`/education360/applicant/${school}?token=${stored}`);
        }
      }).catch(() => {
        localStorage.removeItem(tokenKey(school));
      });
    } else {
      // Create a draft immediately
      admAPI.startApplication({
        tenant_slug: school,
        year_group_applied: "Year 7",
        date_of_birth: "",
        first_name: "",
        last_name: "",
        parents: [{ first_name: "", last_name: "", relationship: "Parent", email: "", phone: "" }],
      }).then(({ data }) => {
        localStorage.setItem(tokenKey(school), data.application_token);
        return admAPI.loadApplicantPortal(data.application_token);
      }).then(({ data }) => setApplicant(data.applicant));
    }
  }, [school, nav]);

  // Autosave debounced
  const patch = (delta) => {
    const merged = { ...applicant, ...delta };
    setApplicant(merged);
    setSaving(true);
    if (debouncer.current) clearTimeout(debouncer.current);
    debouncer.current = setTimeout(async () => {
      try { await admAPI.autosaveApplication(applicant.application_token, delta); }
      finally { setSaving(false); }
    }, 700);
  };

  const submit = async () => {
    try {
      const { data } = await admAPI.submitApplication(applicant.application_token);
      if (!data.submitted) {
        setMissing(data.missing || []);
        toast.error("Please complete the missing items");
        return;
      }
      setSubmitted(true);
      toast.success(`Application submitted — ${data.application_no}`);
    } catch { toast.error("Submission failed"); }
  };

  if (!applicant) return <div className="edu360-root p-10 text-slate-500">Loading application…</div>;
  if (submitted) {
    return (
      <div className="edu360-root min-h-screen bg-[color:var(--hl-page)] grid place-items-center p-6">
        <div className="hl-card p-8 max-w-lg text-center" data-testid="submit-success">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
          <h2 className="font-display font-bold text-2xl mt-3">Application submitted</h2>
          <p className="text-slate-600 mt-2 text-sm">
            Reference <span className="mono font-semibold">{applicant.application_no}</span>. We have emailed an
            acknowledgement. You may return anytime to track progress.
          </p>
          <button onClick={() => nav(`/education360/applicant/${school}?token=${applicant.application_token}`)} className="hl-button-primary text-sm mt-4">
            Open applicant portal
          </button>
        </div>
      </div>
    );
  }

  const parent = (applicant.parents || [])[0] || {};
  const currentStep = STEPS[step];

  return (
    <div className="edu360-root min-h-screen bg-[color:var(--hl-page)]" data-testid="public-apply">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <School className="w-5 h-5 text-[color:var(--hl-navy)]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold">Admissions360™</div>
              <div className="font-display font-bold text-slate-800">{tenant?.name || "Application"}</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 mono flex items-center gap-3">
            <span>Ref {applicant.application_no}</span>
            {saving ? <span className="text-amber-600">Saving…</span> : <span className="text-emerald-600 flex items-center gap-1"><Save className="w-3 h-3" /> Saved</span>}
          </div>
        </div>

        {/* Step indicator */}
        <div className="max-w-4xl mx-auto px-6 pb-4 flex items-center gap-1 overflow-x-auto" data-testid="step-indicator">
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setStep(i)}
              className={`text-[11px] px-3 py-1.5 rounded-full flex items-center gap-1.5 whitespace-nowrap
                ${i === step ? "bg-[color:var(--hl-navy)] text-white" : i < step ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
              data-testid={`step-${s.id}`}
            >
              <s.icon className="w-3 h-3" />
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="hl-card p-6" data-testid={`step-panel-${currentStep.id}`}>
          {currentStep.id === "student" && <StepStudent a={applicant} on={patch} />}
          {currentStep.id === "family" && <StepFamily a={applicant} on={patch} parent={parent} />}
          {currentStep.id === "history" && <StepHistory a={applicant} on={patch} />}
          {currentStep.id === "medical" && <StepMedical a={applicant} on={patch} />}
          {currentStep.id === "essay" && <StepEssay a={applicant} on={patch} />}
          {currentStep.id === "docs" && <StepDocs a={applicant} token={applicant.application_token} onReload={async () => {
            const { data } = await admAPI.loadApplicantPortal(applicant.application_token);
            setApplicant(data.applicant);
          }} />}
          {currentStep.id === "declare" && <StepDeclare a={applicant} on={patch} missing={missing} onSubmit={submit} />}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="hl-button-secondary text-sm disabled:opacity-40" data-testid="btn-prev-step">
            <ChevronLeft className="w-3.5 h-3.5 inline mr-1" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => setStep(step + 1)} className="hl-button-primary text-sm" data-testid="btn-next-step">
              Continue <ChevronRight className="w-3.5 h-3.5 inline ml-1" />
            </button>
          ) : (
            <button onClick={submit} className="hl-button-primary text-sm" data-testid="btn-submit-app">
              <Send className="w-3.5 h-3.5 inline mr-1" /> Submit application
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Steps ---------- */
function T({ label, v, on, type = "text", testId, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <input type={type} value={v || ""} onChange={e => on(e.target.value)} data-testid={testId} className="w-full mt-1 border border-slate-200 rounded px-3 py-2 text-sm" {...rest} />
    </label>
  );
}
function S({ label, v, on, options, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select value={v || ""} onChange={e => on(e.target.value)} data-testid={testId} className="w-full mt-1 border border-slate-200 rounded px-3 py-2 text-sm">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
function TA({ label, v, on, rows = 4, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <textarea rows={rows} value={v || ""} onChange={e => on(e.target.value)} data-testid={testId} className="w-full mt-1 border border-slate-200 rounded px-3 py-2 text-sm" />
    </label>
  );
}

function StepStudent({ a, on }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2 font-display font-bold text-xl text-[color:var(--hl-navy)]">Student details</div>
      <T label="First name" v={a.first_name} on={v => on({ first_name: v })} testId="f-first" />
      <T label="Last name" v={a.last_name} on={v => on({ last_name: v })} testId="f-last" />
      <T label="Middle name" v={a.middle_name} on={v => on({ middle_name: v })} testId="f-middle" />
      <T label="Date of birth" type="date" v={a.date_of_birth} on={v => on({ date_of_birth: v })} testId="f-dob" />
      <S label="Gender" v={a.gender} on={v => on({ gender: v })} options={["", "Male", "Female", "Non-binary", "Prefer not to say"]} testId="f-gender" />
      <T label="Nationality" v={a.nationality} on={v => on({ nationality: v })} testId="f-nationality" />
      <S label="Year group applied" v={a.year_group_applied} on={v => on({ year_group_applied: v })} options={YEAR_GROUPS} testId="f-year" />
      <T label="Entry year" v={a.entry_year} on={v => on({ entry_year: v })} testId="f-entry" />
      <T label="Religion (optional)" v={a.religion} on={v => on({ religion: v })} testId="f-religion" />
      <T label="Languages (comma separated)" v={(a.languages || []).join(", ")} on={v => on({ languages: v.split(",").map(s => s.trim()).filter(Boolean) })} testId="f-langs" />
      <label className="flex items-center gap-2 text-sm border border-slate-200 rounded px-3 py-2">
        <input type="checkbox" checked={!!a.boarding_requested} onChange={e => on({ boarding_requested: e.target.checked })} data-testid="f-boarding" /> Boarding requested
      </label>
      <label className="flex items-center gap-2 text-sm border border-slate-200 rounded px-3 py-2">
        <input type="checkbox" checked={!!a.transport_requested} onChange={e => on({ transport_requested: e.target.checked })} data-testid="f-transport" /> Transport requested
      </label>
      <label className="flex items-center gap-2 text-sm border border-slate-200 rounded px-3 py-2">
        <input type="checkbox" checked={!!a.scholarship_requested} onChange={e => on({ scholarship_requested: e.target.checked })} data-testid="f-scholarship" /> Scholarship application
      </label>
      {a.scholarship_requested && (
        <S label="Scholarship category" v={a.scholarship_category} on={v => on({ scholarship_category: v })} options={["", "Academic", "Music", "Sport", "Art", "All-round"]} testId="f-scholarship-cat" />
      )}
      <T label="Address" v={a.address_line1} on={v => on({ address_line1: v })} testId="f-addr" />
      <T label="City" v={a.city} on={v => on({ city: v })} testId="f-city" />
      <T label="Country" v={a.country} on={v => on({ country: v })} testId="f-country" />
      <T label="Postal code" v={a.postal_code} on={v => on({ postal_code: v })} testId="f-post" />
    </div>
  );
}

function StepFamily({ a, on, parent }) {
  const setParent = (delta) => on({ parents: [{ ...parent, ...delta }] });
  const refs = a.references || [];
  const setRef = (i, delta) => {
    const next = [...refs];
    next[i] = { ...next[i], ...delta };
    on({ references: next });
  };
  const addRef = () => on({ references: [...refs, { name: "", position: "", school: "", email: "" }] });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2 font-display font-bold text-xl text-[color:var(--hl-navy)]">Parent / Guardian</div>
      <T label="First name" v={parent.first_name} on={v => setParent({ first_name: v })} testId="p-first" />
      <T label="Last name" v={parent.last_name} on={v => setParent({ last_name: v })} testId="p-last" />
      <S label="Relationship" v={parent.relationship} on={v => setParent({ relationship: v })} options={["Parent", "Mother", "Father", "Guardian", "Grandparent"]} testId="p-rel" />
      <T label="Email" type="email" v={parent.email} on={v => setParent({ email: v })} testId="p-email" />
      <T label="Phone" v={parent.phone} on={v => setParent({ phone: v })} testId="p-phone" />
      <T label="Occupation" v={parent.occupation} on={v => setParent({ occupation: v })} testId="p-occ" />
      <div className="md:col-span-2">
        <TA label="Home address" v={parent.address} on={v => setParent({ address: v })} testId="p-addr" rows={2} />
      </div>

      <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display font-bold text-xl text-[color:var(--hl-navy)]">References</div>
          <button onClick={addRef} className="hl-button-secondary text-xs" data-testid="btn-add-ref">Add reference</button>
        </div>
        {refs.map((r, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
            <T label={`Name ${i + 1}`} v={r.name} on={v => setRef(i, { name: v })} testId={`ref-${i}-name`} />
            <T label="Position" v={r.position} on={v => setRef(i, { position: v })} testId={`ref-${i}-pos`} />
            <T label="School" v={r.school} on={v => setRef(i, { school: v })} testId={`ref-${i}-sch`} />
            <T label="Email" type="email" v={r.email} on={v => setRef(i, { email: v })} testId={`ref-${i}-email`} />
          </div>
        ))}
        {refs.length === 0 && <div className="text-slate-500 text-sm">Add at least one reference from a current teacher.</div>}
      </div>
    </div>
  );
}

function StepHistory({ a, on }) {
  const schools = a.previous_schools || [];
  const setSchool = (i, delta) => {
    const next = [...schools];
    next[i] = { ...next[i], ...delta };
    on({ previous_schools: next });
  };
  const addSchool = () => on({ previous_schools: [...schools, { name: "", country: "", from_year: "", to_year: "", curriculum: "" }] });
  const grades = a.academic_history || [];
  const setGrade = (i, delta) => {
    const next = [...grades];
    next[i] = { ...next[i], ...delta };
    on({ academic_history: next });
  };
  const addGrade = () => on({ academic_history: [...grades, { subject: "", grade: "", year: "" }] });
  return (
    <div>
      <div className="font-display font-bold text-xl text-[color:var(--hl-navy)] mb-3">Previous schools</div>
      {schools.map((s, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2">
          <T label="School" v={s.name} on={v => setSchool(i, { name: v })} testId={`sch-${i}-name`} />
          <T label="Country" v={s.country} on={v => setSchool(i, { country: v })} testId={`sch-${i}-country`} />
          <T label="From year" v={s.from_year} on={v => setSchool(i, { from_year: v })} testId={`sch-${i}-from`} />
          <T label="To year" v={s.to_year} on={v => setSchool(i, { to_year: v })} testId={`sch-${i}-to`} />
          <T label="Curriculum" v={s.curriculum} on={v => setSchool(i, { curriculum: v })} testId={`sch-${i}-curr`} />
        </div>
      ))}
      <button onClick={addSchool} className="hl-button-secondary text-xs" data-testid="btn-add-school">Add school</button>

      <div className="font-display font-bold text-xl text-[color:var(--hl-navy)] mt-6 mb-3">Recent academic results</div>
      {grades.map((g, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <T label="Subject" v={g.subject} on={v => setGrade(i, { subject: v })} testId={`gr-${i}-subj`} />
          <T label="Grade" v={g.grade} on={v => setGrade(i, { grade: v })} testId={`gr-${i}-grade`} />
          <T label="Year" v={g.year} on={v => setGrade(i, { year: v })} testId={`gr-${i}-year`} />
        </div>
      ))}
      <button onClick={addGrade} className="hl-button-secondary text-xs" data-testid="btn-add-grade">Add grade</button>
    </div>
  );
}

function StepMedical({ a, on }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2 font-display font-bold text-xl text-[color:var(--hl-navy)]">Medical & special requirements</div>
      <TA label="Medical conditions" v={a.medical_conditions} on={v => on({ medical_conditions: v })} testId="m-cond" />
      <TA label="Allergies" v={a.allergies} on={v => on({ allergies: v })} testId="m-allerg" />
      <TA label="Medications" v={a.medications} on={v => on({ medications: v })} testId="m-med" />
      <TA label="Dietary requirements" v={a.dietary_requirements} on={v => on({ dietary_requirements: v })} testId="m-diet" />
      <div className="md:col-span-2">
        <TA label="Special educational needs / learning support" v={a.special_needs} on={v => on({ special_needs: v })} rows={3} testId="m-send" />
      </div>
      <div className="md:col-span-2">
        <TA label="Emergency contact (name, relationship, phone)" v={JSON.stringify(a.emergency_contact || {})} on={v => { try { on({ emergency_contact: JSON.parse(v) }); } catch { on({ emergency_contact: { note: v } }); } }} rows={2} testId="m-emerg" />
      </div>
    </div>
  );
}

function StepEssay({ a, on }) {
  return (
    <div className="space-y-4">
      <div className="font-display font-bold text-xl text-[color:var(--hl-navy)]">Personal statement</div>
      <p className="text-slate-500 text-sm">Tell us about your interests, achievements, and why you are applying (300–800 words).</p>
      <TA v={a.essay} on={v => on({ essay: v })} rows={10} label="Personal statement" testId="e-essay" />
      <TA v={a.achievements} on={v => on({ achievements: v })} rows={4} label="Achievements & awards" testId="e-ach" />
      <TA v={a.hobbies} on={v => on({ hobbies: v })} rows={3} label="Hobbies & interests" testId="e-hob" />
    </div>
  );
}

const DOC_TYPES = ["passport", "birth_cert", "photo", "transcript", "reference", "visa", "other"];

function StepDocs({ a, token, onReload }) {
  const [type, setType] = useState("passport");
  const [name, setName] = useState("");
  const upload = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      try {
        await admAPI.uploadDocument(token, { type, name: name || file.name, data_url: dataUrl });
        toast.success("Document attached");
        setName("");
        onReload();
      } catch { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <div className="font-display font-bold text-xl text-[color:var(--hl-navy)] mb-3">Supporting documents</div>
      <div className="hl-card p-4 flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200">
        <label className="text-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500">Document type</div>
          <select value={type} onChange={e => setType(e.target.value)} className="border border-slate-200 rounded px-2 py-1 mt-1 text-sm" data-testid="d-type">
            {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label className="text-sm flex-1 min-w-[220px]">
          <div className="text-xs uppercase tracking-widest text-slate-500">Label (optional)</div>
          <input value={name} onChange={e => setName(e.target.value)} className="border border-slate-200 rounded px-2 py-1 mt-1 text-sm w-full" data-testid="d-name" />
        </label>
        <label className="hl-button-primary text-xs cursor-pointer">
          Attach file
          <input type="file" hidden onChange={e => upload(e.target.files?.[0])} data-testid="d-file" />
        </label>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
        {(a.documents || []).map((d, i) => (
          <div key={i} className="border border-slate-200 rounded p-3 text-sm flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-slate-400" />
            <div className="flex-1">
              <div className="font-medium">{d.name}</div>
              <div className="text-xs text-slate-500 uppercase">{d.type}</div>
            </div>
            {d.verified ? <span className="text-emerald-600 text-xs">Verified</span> : <span className="text-amber-600 text-xs">Pending</span>}
          </div>
        ))}
        {(a.documents || []).length === 0 && <div className="text-slate-500 text-sm col-span-full">No documents yet.</div>}
      </div>
    </div>
  );
}

function StepDeclare({ a, on, missing, onSubmit }) {
  return (
    <div>
      <div className="font-display font-bold text-xl text-[color:var(--hl-navy)] mb-3">Declaration & digital signature</div>
      <div className="hl-card p-4 bg-slate-50 border border-slate-200 text-sm text-slate-700 mb-4">
        I confirm the information provided is true and complete to the best of my knowledge. I understand
        that any false statement may result in the application being withdrawn or the student being asked
        to leave the school. I consent to the school processing the data provided for admissions purposes.
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!a.declaration_accepted} onChange={e => on({ declaration_accepted: e.target.checked })} data-testid="d-accept" />
        I accept the terms of this declaration.
      </label>
      <div className="mt-3">
        <T label="Digital signature (full name)" v={a.digital_signature} on={v => on({ digital_signature: v })} testId="d-sign" />
      </div>
      {missing.length > 0 && (
        <div className="hl-card p-4 mt-4 border-l-4 border-l-amber-500 flex items-start gap-3" data-testid="missing-block">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
          <div className="text-sm text-amber-800">
            <b>Please complete before submitting:</b>
            <ul className="list-disc ml-4 mt-1">
              {missing.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
